import { useState, useCallback } from 'react'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { mdiPencil, mdiPlus, mdiTrashCanOutline } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog'
import { $api } from '../../api/queryClient'
import { useCan } from '../../contexts/PermissionsContext'
import { ConditionSummary } from './ConditionSummary'
import { DerivationRuleEditDialog } from './DerivationRuleEditDialog'
import { EmptyState } from '../ui/empty-state'

interface DerivationRulesTableProps {
  org: string
  uid: string
  rules: any[]
  onRulesChange: () => void
}

export function DerivationRulesTable({ org, uid, rules, onRulesChange }: DerivationRulesTableProps) {
  const [editingRuleIndex, setEditingRuleIndex] = useState<number | null>(null)
  const [isAddingRule, setIsAddingRule] = useState(false)
  // The decision table is part of the derivation definition: writing it needs
  // `configuration.write` (spec 2026-09-04-02, "Gating").
  const canWriteConfiguration = useCan('configuration.write')
  const [deleteRuleIndex, setDeleteRuleIndex] = useState<number | null>(null)

  const updateMutation = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/derivations/{derivationUid}', {
    onSuccess: () => onRulesChange()
  })

  const handleSaveRule = useCallback((index: number, updatedRule: any) => {
    const newRules = [...rules]
    if (index >= 0 && index < newRules.length) {
      newRules[index] = updatedRule
    }
    // Sort by priority
    newRules.sort((a: any, b: any) => (a.priority || 0) - (b.priority || 0))
    updateMutation.mutate({
      params: { path: { orgCode: org, derivationUid: uid } },
      body: { decision_table: newRules } as any
    })
    setEditingRuleIndex(null)
  }, [rules, org, uid, updateMutation])

  const handleAddRule = useCallback((newRule: any) => {
    const newRules = [...rules, newRule]
    newRules.sort((a: any, b: any) => (a.priority || 0) - (b.priority || 0))
    updateMutation.mutate({
      params: { path: { orgCode: org, derivationUid: uid } },
      body: { decision_table: newRules } as any
    })
    setIsAddingRule(false)
  }, [rules, org, uid, updateMutation])

  const handleDeleteRule = useCallback((index: number) => {
    const newRules = rules.filter((_: any, i: number) => i !== index)
    updateMutation.mutate({
      params: { path: { orgCode: org, derivationUid: uid } },
      body: { decision_table: newRules } as any
    })
    setDeleteRuleIndex(null)
  }, [rules, org, uid, updateMutation])

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">Decision Table ({rules.length} rules)</h3>
        {canWriteConfiguration && (
          <Button
            onClick={() => setIsAddingRule(true)}
            size="sm"
            data-testid="add-rule-btn"
          >
            <AppIcon path={mdiPlus} size="sm" className="mr-1" />
            Add Rule
          </Button>
        )}
      </div>

      {rules.length === 0 ? (
        <EmptyState variant="inline" title="No rules defined" />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">#</TableHead>
              <TableHead className="w-20">Priority</TableHead>
              <TableHead>Comment</TableHead>
              <TableHead>Conditions</TableHead>
              <TableHead>Output</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rules.map((rule: any, index: number) => (
              <TableRow key={index}>
                <TableCell className="text-muted-foreground">{index + 1}</TableCell>
                <TableCell>
                  <Badge variant="outline">{rule.priority}</Badge>
                </TableCell>
                <TableCell className="text-sm">{rule.comment || '-'}</TableCell>
                <TableCell className="max-w-md truncate">
                  <ConditionSummary conditions={rule.conditions} />
                </TableCell>
                <TableCell>
                  <code className="text-sm bg-muted px-1 py-0.5 rounded">
                    {typeof rule.output === 'string' ? rule.output : JSON.stringify(rule.output)}
                  </code>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    {canWriteConfiguration && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingRuleIndex(index)}
                          data-testid={`edit-rule-${index}`}
                        >
                          <AppIcon path={mdiPencil} size="xs" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteRuleIndex(index)}
                          data-testid={`delete-rule-${index}`}
                        >
                          <AppIcon path={mdiTrashCanOutline} size="xs" />
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {editingRuleIndex !== null && (
        <DerivationRuleEditDialog
          rule={rules[editingRuleIndex]}
          onSave={(updatedRule) => handleSaveRule(editingRuleIndex, updatedRule)}
          onCancel={() => setEditingRuleIndex(null)}
        />
      )}

      {isAddingRule && (
        <DerivationRuleEditDialog
          rule={{ priority: (rules.length > 0 ? Math.max(...rules.map((r: any) => r.priority || 0)) + 10 : 10), comment: '', conditions: {}, output: '' }}
          onSave={handleAddRule}
          onCancel={() => setIsAddingRule(false)}
        />
      )}

      <AlertDialog open={deleteRuleIndex !== null} onOpenChange={() => setDeleteRuleIndex(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Rule</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete rule #{deleteRuleIndex !== null ? deleteRuleIndex + 1 : ''}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-rule">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteRuleIndex !== null && handleDeleteRule(deleteRuleIndex)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-rule"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
